import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken } from '../utils/auth';
import { toast } from 'sonner';
import {
  ArrowRight, CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronUp,
  Send, Loader2, Mail, Hammer,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });
const isPlaceholder = (r) => !r || /example\.com|test-vendor|placeholder/i.test(r);

const RfqApproveRow = ({ projectId, rfq, onDone }) => {
  const [recipient, setRecipient] = useState(rfq.recipient || '');
  const [sending, setSending] = useState(false);
  const bad = isPlaceholder(recipient);

  const approve = async () => {
    if (!recipient || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast.error('Enter the vendor\'s real email before approving');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/idea/projects/${projectId}/rfqs/${rfq.email_id}/approve`,
        { recipient }, auth());
      const st = r.data?.send_result?.status || r.data?.tracking?.status;
      if (st === 'sent') toast.success(`RFQ sent to ${rfq.vendor}`);
      else if (st === 'queued') toast(`RFQ queued for ${rfq.vendor} — will send when email is configured`);
      else toast.error(`Could not send: ${st || 'unknown'}`);
      onDone(rfq.email_id, st);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
    setSending(false);
  };

  return (
    <div className="rounded-xl bg-black/30 border border-white/[0.07] p-3" data-testid="rfq-approve-row">
      <div className="flex items-center gap-2 min-w-0">
        <Mail className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        <p className="text-[13px] font-semibold text-zinc-200 truncate">{rfq.vendor || 'Vendor'}</p>
      </div>
      <p className="text-[11px] text-zinc-500 truncate mt-0.5">{rfq.subject}</p>
      <div className="flex items-center gap-2 mt-2">
        <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
          placeholder="vendor email"
          className={`flex-1 min-w-0 bg-black/40 border rounded-lg px-2.5 py-1.5 text-[12px] text-white placeholder-zinc-600 ${bad ? 'border-yellow-500/50' : 'border-white/10'}`}
          data-testid="rfq-recipient-input" />
        <button onClick={approve} disabled={sending}
          className="flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg bg-emerald-500 text-black text-[12px] font-bold disabled:opacity-50 hover:bg-emerald-400 transition-colors"
          data-testid="rfq-approve-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Approve & send
        </button>
      </div>
      {bad && (
        <p className="text-[10px] text-yellow-400 mt-1.5 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" /> Placeholder address — replace it with the vendor's real quotes email before approving.
        </p>
      )}
    </div>
  );
};

export const PipelineBoard = ({ onSelect }) => {
  const [board, setBoard] = useState([]);
  const [approving, setApproving] = useState(null); // project id with open approvals
  const navigate = useNavigate();

  const load = () => {
    axios.get(`${API_URL}/api/idea/pipeline/board`, auth())
      .then((r) => setBoard(r.data.projects || []))
      .catch(() => {});
  };
  useEffect(load, []);

  const onRfqDone = (projectId) => (emailId, status) => {
    setBoard((prev) => prev.map((p) => {
      if (p.id !== projectId) return p;
      const pending = (p.pending_rfqs || []).filter((r) => r.email_id !== emailId);
      return {
        ...p,
        pending_rfqs: pending,
        rfq_drafts_pending: pending.length,
        rfq_sent: p.rfq_sent + (status === 'sent' ? 1 : 0),
      };
    }));
  };

  if (board.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="pipeline-board">
      <p className="text-[11px] uppercase tracking-widest text-zinc-600 font-bold">Execution board — next action per project</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {board.map((p) => (
          <div key={p.id}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 hover:border-amber-500/30 transition-colors"
            data-testid="pipeline-project-card">
            <button onClick={() => onSelect && onSelect(p.id)} className="w-full text-left" data-testid="pipeline-project-open">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[14px] font-semibold text-zinc-100 truncate">{p.title}</p>
                {p.readiness && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                    p.readiness === 'ready_to_order' ? 'bg-emerald-500/15 text-emerald-400'
                      : p.readiness === 'partially_verified' ? 'bg-amber-500/15 text-amber-400'
                        : 'bg-red-500/15 text-red-400'}`}>
                    {p.readiness.replace(/_/g, ' ')}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1 mt-3 overflow-x-auto scrollbar-hide" data-testid="milestone-row">
                {p.milestones.map((m, i) => (
                  <React.Fragment key={m.key}>
                    {i > 0 && <span className={`h-px w-3 shrink-0 ${m.done ? 'bg-emerald-500/50' : 'bg-white/10'}`} />}
                    <span className="flex items-center gap-1 shrink-0" title={m.label}>
                      {m.done
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        : <Circle className="w-3.5 h-3.5 text-zinc-700" />}
                      <span className={`text-[10px] ${m.done ? 'text-zinc-400' : 'text-zinc-600'}`}>{m.label}</span>
                    </span>
                  </React.Fragment>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-3 rounded-xl bg-amber-500/[0.07] border border-amber-500/15 px-3 py-2" data-testid="next-action-chip">
                {p.flags_high_open > 0
                  ? <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                  : <ArrowRight className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                <span className="text-[12px] text-amber-200/90 leading-snug">{p.next_action}</span>
              </div>
            </button>

            <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-600 flex-wrap">
              {p.rfq_drafts_pending > 0 && (
                <button onClick={() => setApproving(approving === p.id ? null : p.id)}
                  className="flex items-center gap-1 text-amber-400 font-bold hover:text-amber-300 transition-colors"
                  data-testid="rfq-open-approvals-btn">
                  {p.rfq_drafts_pending} awaiting your approval
                  {approving === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
              {p.rfq_sent > 0 && <span>{p.rfq_sent} RFQs sent</span>}
              {p.open_questions > 0 && <span>{p.open_questions} open questions</span>}
              <button onClick={() => navigate(`/inventions/${p.id}/builders`)}
                className="flex items-center gap-1 text-emerald-400 font-bold hover:text-emerald-300 transition-colors"
                data-testid="find-builders-btn">
                <Hammer className="w-3 h-3" /> Bring it to life
              </button>
              {p.est_cost?.est_total_low != null && (
                <span className="ml-auto font-mono">est ${Math.round(p.est_cost.est_total_low)}–${Math.round(p.est_cost.est_total_high)}</span>
              )}
            </div>

            {approving === p.id && (p.pending_rfqs || []).length > 0 && (
              <div className="mt-3 space-y-2" data-testid="rfq-approvals-panel">
                <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-bold">Approve to send — nothing leaves without you</p>
                {p.pending_rfqs.map((r) => (
                  <RfqApproveRow key={r.email_id} projectId={p.id} rfq={r} onDone={onRfqDone(p.id)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
